import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Star, Eye, Clock, Phone, Utensils, Images, Handshake, ShieldCheck, Info, MessageSquare, Instagram, Globe, Target, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { fmt, euro } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { LocationMap } from "../components/LocationMap";

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [showcase, setShowcase] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get(`/businesses/${id}`).then(({ data }) => setB(data.business)).catch(() => toast.error("Introuvable"));
    api.post(`/businesses/${id}/view`).catch(() => {});
    api.get(`/businesses/${id}/showcase`).then(({ data }) => setShowcase(data.deliverables)).catch(() => {});
    api.get("/campaigns").then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  }, [id]);

  if (!b) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;

  const activeCampaigns = campaigns.filter((c) => c.status === "open" && c.business_name && b.name && c.business_name === b.name);
  const socials = b.socials || {};
  const socialLinks = [
    { url: socials.instagram, Icon: Instagram, key: "instagram" },
    { url: socials.website, Icon: Globe, key: "website" },
  ].filter((l) => l.url);

  return (
    <div className="animate-fade-in space-y-5">
      <div className="relative -mx-4 -mt-4">
        <img src={b.image} alt={b.name} className="h-44 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink" />
        <button onClick={() => navigate(-1)} data-testid="business-back-btn" className="absolute left-4 top-4 grid h-9 w-9 place-items-center rounded-full glass text-white"><ArrowLeft size={18} /></button>
        <div className="absolute bottom-3 left-4 right-4">
          <h1 className="font-heading text-2xl font-extrabold">{b.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-200">
            <span className="rounded-full bg-ink/70 px-2 py-0.5">{b.category}</span>
            <span className="flex items-center gap-1 text-gold"><Star size={12} fill="currentColor" /> {b.rating}</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {b.city}</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {fmt(b.views || 0)} {t("pageViews")}</span>
          </div>
        </div>
      </div>

      {/* About */}
      {(b.about || b.description) && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="business-about">
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold"><Info size={16} className="text-gold" /> {t("aboutTitle")}</h3>
          <p className="text-sm leading-relaxed text-slate-300">{b.about || b.description}</p>
          {socialLinks.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-400">{t("followUs")}:</span>
              {socialLinks.map(({ url, Icon, key }) => (
                <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`biz-social-${key}`}
                  className="grid h-8 w-8 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300 transition-colors hover:border-gold hover:text-gold"><Icon size={15} /></a>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-2 rounded-xl border border-cardline bg-card2/60 p-3 text-slate-300"><Clock size={15} className="text-gold" /> {b.hours || "—"}</div>
        <div className="flex items-center gap-2 rounded-xl border border-cardline bg-card2/60 p-3 text-slate-300"><Phone size={15} className="text-gold" /> {b.phone || "—"}</div>
      </div>

      {/* Detailed hours */}
      {b.hours_detail?.length > 0 && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="business-hours">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold"><Clock size={16} className="text-gold" /> {t("openingHours")}</h3>
          <div className="space-y-1.5">
            {b.hours_detail.map((row, i) => (
              <div key={i} className="flex items-center justify-between border-b border-cardline/40 pb-1.5 text-sm last:border-0 last:pb-0">
                <span className="text-slate-400">{row.d}</span><span className="font-semibold text-slate-200">{row.h}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Location map */}
      {b.lat != null && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="business-location">
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold"><MapPin size={16} className="text-gold" /> {t("locationTitle")}</h3>
          <LocationMap lat={b.lat} lng={b.lng} color="#D9A441" height="13rem" />
        </div>
      )}

      {/* Active campaigns */}
      {activeCampaigns.length > 0 && (
        <div data-testid="business-campaigns">
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold"><Target size={16} className="text-gold" /> {t("activeCampaigns")}</h3>
          <div className="space-y-2">
            {activeCampaigns.map((c) => (
              <button key={c.id} onClick={() => navigate("/campaigns")} data-testid={`biz-campaign-${c.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-cardline bg-card2/60 p-3 text-left transition-colors hover:border-gold/60">
                <div className="min-w-0">
                  <div className="truncate font-heading text-sm font-semibold">{c.title}</div>
                  <div className="truncate text-xs text-slate-400">{c.reward_value || euro(c.budget)}</div>
                </div>
                <ChevronRight size={16} className="shrink-0 text-slate-500" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Menu */}
      {b.menu?.length > 0 && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="business-menu">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold"><Utensils size={16} className="text-gold" /> {t("menu")}</h3>
          <div className="space-y-2.5">
            {b.menu.map((m, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b border-cardline/50 pb-2.5 last:border-0 last:pb-0">
                <div><div className="text-sm font-semibold">{m.name}</div><div className="text-xs text-slate-400">{m.desc}</div></div>
                <div className="shrink-0 font-heading font-bold text-gold">{euro(m.price)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gallery */}
      {b.gallery?.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold"><Images size={16} className="text-gold" /> {t("gallery")}</h3>
          <div className="grid grid-cols-3 gap-2">
            {b.gallery.map((g, i) => <img key={i} src={g} alt="" className="aspect-square w-full rounded-xl border border-cardline object-cover" />)}
          </div>
        </div>
      )}

      {/* Reviews */}
      {b.reviews?.length > 0 && (
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4" data-testid="business-reviews">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold"><MessageSquare size={16} className="text-gold" /> {t("reviewsTitle")}</h3>
          <div className="space-y-3">
            {b.reviews.map((r, i) => (
              <div key={i} className="rounded-xl bg-ink2/50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{r.author}</span>
                  <span className="flex text-gold">{[0,1,2,3,4].map((s) => <Star key={s} size={11} fill={s < r.rating ? "currentColor" : "none"} />)}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-300">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Showcase (delivered content) */}
      {showcase.length > 0 && (
        <div data-testid="business-showcase">
          <h3 className="mb-2 font-heading text-sm font-semibold">{t("realisationsShowcase")}</h3>
          <div className="grid grid-cols-3 gap-2">
            {showcase.map((d) => (
              <div key={d.id} className="aspect-square overflow-hidden rounded-xl border border-cardline bg-ink2">
                {d.kind === "video"
                  ? <video src={`${api.defaults.baseURL}/deliverables/${d.id}/download?auth=${localStorage.getItem("lc_token") || ""}`} muted loop autoPlay playsInline className="h-full w-full object-cover" />
                  : <img src={`${api.defaults.baseURL}/deliverables/${d.id}/download?auth=${localStorage.getItem("lc_token") || ""}`} alt="" className="h-full w-full object-cover" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collaboration contract */}
      <div className="rounded-2xl border border-gold/30 bg-gold/5 p-4" data-testid="business-contract">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-gold"><ShieldCheck size={16} /> {t("collabTerms")}</h3>
        <p className="mt-2 text-xs leading-relaxed text-slate-300">{b.contract_terms}</p>
        {user?.role === "creator" && (
          <button onClick={() => navigate("/campaigns")} data-testid="business-apply-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-3 font-bold text-ink">
            <Handshake size={16} /> {t("applyToCollab")}
          </button>
        )}
      </div>
    </div>
  );
}
